import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  FinanceCountDimensionValueMembershipsDocument,
  FinanceDeleteDimensionValueDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type ModalProps = React.ComponentProps<typeof Modal>

interface Props {
  id: number
  onClose: (success?: boolean) => void
}

const DeleteDimensionValueModal = ({ id, onClose }: Props) => {
  const [deleting, setDeleting] = React.useState(false)
  const { company } = useCurrentSession()

  const { data, error } = useQuery(FinanceCountDimensionValueMembershipsDocument, {
    variables: {
      dimensionValueId: id,
      companyId: company.id,
    },
  })
  const loading = !data && !error

  const [deleteDimensionValue] = useMutation(FinanceDeleteDimensionValueDocument, {
    extractorFn: (deleteData) => deleteData.financeMutations.deleteFinanceCustomDimensionValue,
  })

  const handleSubmit = async () => {
    setDeleting(true)
    const result = await deleteDimensionValue({
      variables: {
        id: Number(id),
        companyId: company.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.modals.delete_dimension_value.error'),
      })
    })

    if (handled.success) {
      onClose(true)
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.modals.delete_dimension_value.success'),
      })
    }
    setDeleting(false)
  }
  const loadingProps: ModalProps = {
    illustration: 'delete',
    title: '',
    description: '',
    onClose: onClose,
  }

  const removingProps: ModalProps = {
    illustration: 'delete',
    title: i18n.t('finance.modals.delete_dimension_value.title'),
    description: i18n.t('finance.modals.delete_dimension_value.description'),
    onClose: onClose,
    onSubmit: handleSubmit,
    footer: (
      <Button disabled={deleting} submit>
        {i18n.t('finance.buttons.delete')}
      </Button>
    ),
  }

  const removingNotAllowedProps: ModalProps = {
    illustration: 'alert',
    title: i18n.t('finance.modals.delete_dimension_value.title_not_allowed'),
    description: i18n.t('finance.modals.delete_dimension_value.description_not_allowed'),
    onClose: onClose,
  }

  const modalProps = loading
    ? loadingProps
    : data?.finance.customDimensionMembershipsConnection.totalCount === 0
    ? removingProps
    : removingNotAllowedProps

  return <Modal {...modalProps}>{loading && <Loading />}</Modal>
}

export default DeleteDimensionValueModal
